/**
 * Structural deep-equality, for the module's "has this template changed"
 * questions — `isTemplateDirty`, `templateHasMoved`, and the undo history's
 * `classifyHistoryChange`.
 *
 * Its own file so none of those owns it, and module-private (never on the
 * barrel) because nothing outside needs it: this repo has no app-wide deep
 * compare, and adding one on the strength of callers in one module would be
 * a shared contract nobody asked for.
 */

/** Structural deep-equality (order-independent for object keys) — avoids the
 *  key-order false-positives a raw JSON.stringify compare produces once either
 *  side has been rebuilt via spreads or re-serialized by a different code path.
 *  Handles the built-in wrapper types (Date / RegExp / Map / Set) so a change to
 *  one is not silently treated as "clean"; plain objects and arrays compare
 *  structurally. */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;

  // An array and a plain object (or two different built-ins) are never equal.
  if (Object.getPrototypeOf(a) !== Object.getPrototypeOf(b)) return false;

  if (a instanceof Date) return b instanceof Date && a.getTime() === b.getTime();
  if (a instanceof RegExp)
    return b instanceof RegExp && a.source === b.source && a.flags === b.flags;

  if (Array.isArray(a)) {
    const bb = b as unknown[];
    if (a.length !== bb.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], bb[i])) return false;
    }
    return true;
  }

  if (a instanceof Map) {
    const bb = b as Map<unknown, unknown>;
    if (a.size !== bb.size) return false;
    for (const [k, v] of a) {
      if (!bb.has(k) || !deepEqual(v, bb.get(k))) return false;
    }
    return true;
  }

  if (a instanceof Set) {
    const bb = b as Set<unknown>;
    if (a.size !== bb.size) return false;
    for (const v of a) {
      if (!bb.has(v)) return false;
    }
    return true;
  }

  const oa = a as Record<string, unknown>;
  const ob = b as Record<string, unknown>;
  const ka = Object.keys(oa);
  const kb = Object.keys(ob);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (!Object.prototype.hasOwnProperty.call(ob, k)) return false;
    if (!deepEqual(oa[k], ob[k])) return false;
  }
  return true;
}
